'use client';

import { useWishlist } from '@/hooks/useWishlist';
import { useOffers } from '@/hooks/useOffers';
import type { Property } from '@/lib/api/clients/properties';
import { cn } from '@/lib/utils';
import { useTenant } from '@/contexts/TenantContext';
import type { WishlistItem, Offer } from '@/types/customer';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import { FormatPrice } from '@/components/ui/FormatPrice';
import { extractBestPrice } from '@/lib/utils/price';
import { PropertyViewingModal } from '@/components/properties/PropertyViewingModal';
import { OfferForm } from '@/components/customer/OfferForm';
import {
  AlertCircle,
  ArrowRight,
  Bath,
  Bed,
  Calendar,
  CheckCircle2,
  ExternalLink,
  Gavel,
  Heart,
  Loader2,
  MapPin,
  Maximize,
  Send,
  Share2,
  Tag,
  X,
  XCircle,
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/Button';

const useLocalizedHref = () => {
  const pathname = usePathname();
  return (path: string): string => {
    const match = pathname.match(/^\/([a-z]{2})(\/|$)/);
    const locale = match ? match[1] : 'en';
    return `/${locale}${path}`;
  };
};

interface WishlistItemDisplay {
  id: string;
  wishlistId: string;
  entityId: string;
  entityType: string;
  title: string;
  location: string;
  price: number | null;
  currency: string;
  image: string;
  beds?: number;
  baths?: number;
  area?: number;
  areaUnit?: string;
  status: 'active' | 'sold' | 'under-offer';
  propertyTypeName?: string;
  notes?: string;
  listingId?: string;
  askingPrice?: number;
  askingCurrency?: string;
}

function buildLocationString(location?: Property['location'] | null): string {
  if (!location) return 'Location not available';

  const parts: string[] = [];
  if (location.neighbourhood) parts.push(location.neighbourhood);
  else if (location.suburb) parts.push(location.suburb);
  if (location.city) parts.push(location.city);
  if (location.region) parts.push(location.region);
  if (location.country) parts.push(location.country);

  return parts.length > 0 ? parts.join(', ') : 'Location not available';
}

function getHeroImageUrl(property: Property): string | null {
  if (property.primaryImageUrl) return property.primaryImageUrl;
  if (property.heroMedia?.url) return property.heroMedia.url;

  const heroLink = property.mediaLinks?.find(
    (ml) => ml.usageContext === 'PRIMARY_HERO'
  );
  if (heroLink?.media?.url) return heroLink.media.url;

  const firstImage = property.mediaLinks?.find(
    (ml) => ml.media?.mimeType?.startsWith('image/') || ml.media?.mediaType === 'IMAGE'
  );
  return firstImage?.media?.url ?? null;
}

function mapPropertyStatus(status: string): 'active' | 'sold' | 'under-offer' {
  const lower = status.toLowerCase();
  if (lower === 'sold' || lower === 'completed') return 'sold';
  if (lower.includes('offer') || lower.includes('pending')) return 'under-offer';
  return 'active';
}

function transformWithProperty(
  item: WishlistItem,
  wishlistId: string,
  property: Property
): WishlistItemDisplay {
  const bestPrice = extractBestPrice(property);

  const price = bestPrice?.amount ?? null;
  const currency = bestPrice?.currency ?? property.currency ?? DEFAULT_CURRENCY;

  const result: WishlistItemDisplay = {
    id: item.id,
    wishlistId,
    entityId: item.entityId,
    entityType: item.entityType,
    title: property.name || `Saved ${item.entityType}`,
    location: buildLocationString(property.location),
    price,
    currency,
    image: getHeroImageUrl(property) || '',
    status: mapPropertyStatus(property.status),
    ...(property.propertyType?.name ? { propertyTypeName: property.propertyType.name } : {}),
    notes: item.notes ?? '',
  };

  if (property.bedrooms != null) result.beds = property.bedrooms;
  if (property.bathrooms != null) result.baths = property.bathrooms;
  const area = property.totalFloorArea ?? property.landArea;
  if (area != null) result.area = area;
  const areaUnit = property.totalFloorAreaUnit || property.landAreaUnit;
  if (areaUnit) result.areaUnit = areaUnit;

  const firstListing = property.managedAsset?.listings?.[0];
  if (firstListing?.id) {
    result.listingId = firstListing.id;
    const salePrice = firstListing.saleDetails?.primaryPrice;
    if (salePrice) {
      result.askingPrice = salePrice.amount;
      result.askingCurrency = salePrice.currency;
    }
  }

  return result;
}

function transformFallback(
  item: WishlistItem,
  wishlistId: string
): WishlistItemDisplay {
  return {
    id: item.id,
    wishlistId,
    entityId: item.entityId,
    entityType: item.entityType,
    title: `Saved ${item.entityType.charAt(0) + item.entityType.slice(1).toLowerCase()}`,
    location: 'Location not available',
    price: null,
    currency: DEFAULT_CURRENCY,
    image: '',
    status: 'active',
    notes: item.notes ?? '',
  };
}

function formatArea(area: number, unit?: string): string {
  const unitLabel =
    unit === 'SQM' ? 'm²' :
    unit === 'SQFT' ? 'sq ft' :
    unit === 'ACRES' ? 'acres' :
    unit === 'HECTARES' ? 'ha' :
    'm²';
  return `${area.toLocaleString()} ${unitLabel}`;
}

// ---------------------------------------------------------------------------
// SavedProperties (page-level component)
// ---------------------------------------------------------------------------

export const SavedProperties: React.FC = () => {
  const { wishlists, isLoading: isLoadingWishlists, getWishlist, removeFromWishlist } = useWishlist();
  const { offers, refresh: refreshOffers } = useOffers();
  const { organisationId } = useTenant();
  const [allItems, setAllItems] = useState<WishlistItemDisplay[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [viewingProperty, setViewingProperty] = useState<WishlistItemDisplay | null>(null);
  const [offerProperty, setOfferProperty] = useState<WishlistItemDisplay | null>(null);
  const getHref = useLocalizedHref();
  const fetchedForRef = useRef<string>('');

  // Build a map of listingId -> best active offer for quick lookup
  const offersByListing = useMemo(() => {
    const map = new Map<string, Offer>();
    for (const offer of offers) {
      if (!offer.listingId) continue;
      const existing = map.get(offer.listingId);
      if (!existing || new Date(offer.updatedAt) > new Date(existing.updatedAt)) {
        map.set(offer.listingId, offer);
      }
    }
    return map;
  }, [offers]);

  const fetchAllItems = useCallback(async () => {
    if (wishlists.length === 0) {
      setAllItems([]);
      return;
    }

    const wishlistKey = wishlists.map((w) => `${w.id}:${w.itemCount}`).join(',');
    if (fetchedForRef.current === wishlistKey) return;
    fetchedForRef.current = wishlistKey;

    setIsLoadingItems(true);

    try {
      const wishlistPromises = wishlists.map(async (wishlist) => {
        if (wishlist.itemCount === 0) return [];

        try {
          const wishlistWithItems = await getWishlist(wishlist.id);
          if (!wishlistWithItems?.items) return [];
          return wishlistWithItems.items.map(
            (item) => ({ item, wishlistId: wishlist.id })
          );
        } catch (err) {
          console.error(`[SavedProperties] Failed to fetch wishlist ${wishlist.id}:`, err);
          return [];
        }
      });

      const results = await Promise.all(wishlistPromises);
      const allWishlistItems = results.flat();

      if (allWishlistItems.length === 0) {
        setAllItems([]);
        return;
      }

      const propertyItems = allWishlistItems.filter(
        (wi) => wi.item.entityType === 'PROPERTY'
      );

      const propertyIds = [...new Set(propertyItems.map((wi) => wi.item.entityId))];

      const propertyMap = new Map<string, Property>();

      if (propertyIds.length > 0) {
        try {
          const resp = await fetch('/api/properties', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              organisationId,
              propertyIds,
              includeRelations: true,
            }),
          });

          if (resp.ok) {
            const { properties } = await resp.json() as {
              properties: Record<string, Property>;
              errors: Record<string, string>;
            };
            for (const [id, property] of Object.entries(properties)) {
              propertyMap.set(id, property);
            }
          }
        } catch (err) {
          console.error('[SavedProperties] Failed to fetch property details:', err);
        }
      }

      const displayItems = allWishlistItems.map(({ item, wishlistId }) => {
        const property = propertyMap.get(item.entityId);
        if (property) {
          return transformWithProperty(item, wishlistId, property);
        }
        return transformFallback(item, wishlistId);
      });

      setAllItems(displayItems);
    } catch (err) {
      console.error('[SavedProperties] Failed to fetch wishlist items:', err);
      setAllItems([]);
    } finally {
      setIsLoadingItems(false);
    }
  }, [wishlists, getWishlist, organisationId]);

  useEffect(() => {
    if (!isLoadingWishlists && wishlists.length > 0) {
      fetchAllItems();
    } else if (wishlists.length === 0) {
      setAllItems([]);
    }
  }, [wishlists, isLoadingWishlists, fetchAllItems]);

  const totalCount = wishlists.reduce((acc, w) => acc + w.itemCount, 0);
  const isLoading = isLoadingWishlists || isLoadingItems;

  const handleRemove = async (wishlistId: string, itemId: string) => {
    setRemovingIds((prev) => new Set(prev).add(itemId));
    const success = await removeFromWishlist(wishlistId, itemId);
    if (success) {
      setAllItems((prev) => prev.filter((item) => item.id !== itemId));
    }
    setRemovingIds((prev) => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out] pb-12">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-semibold text-stone-900 tracking-tight">Saved Properties</h2>
          <p className="text-stone-500 mt-2 text-lg font-light">
            Your curated collection of potential homes.
          </p>
        </div>
        <div className="hidden md:block">
          <span className="text-sm font-medium text-stone-500">
            {totalCount} {totalCount === 1 ? 'Property' : 'Properties'} Saved
          </span>
        </div>
      </div>

      {allItems.length > 0 ? (
        <div className="flex flex-col gap-6">
          {allItems.map((property) => (
            <PropertyCard
              key={property.id}
              property={property}
              activeOffer={property.listingId ? offersByListing.get(property.listingId) ?? null : null}
              isRemoving={removingIds.has(property.id)}
              onRemove={() => handleRemove(property.wishlistId, property.id)}
              onScheduleViewing={() => setViewingProperty(property)}
              onMakeOffer={() => setOfferProperty(property)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-stone-50 rounded-3xl border border-dashed border-stone-200">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
            <Heart size={24} className="text-stone-300" />
          </div>
          <h3 className="text-lg font-medium text-stone-900">No saved properties</h3>
          <p className="text-stone-500 text-sm mt-1">
            Browse our listings to add to your wishlist.
          </p>
          <Link href={getHref('/properties')}>
            <Button className="mt-6">Browse Properties</Button>
          </Link>
        </div>
      )}

      {viewingProperty && (
        <PropertyViewingModal
          propertyId={viewingProperty.entityId}
          propertyName={viewingProperty.title}
          organisationId={organisationId}
          onClose={() => setViewingProperty(null)}
        />
      )}

      {offerProperty && (
        <OfferModal
          property={offerProperty}
          onClose={() => setOfferProperty(null)}
          onSuccess={() => {
            setOfferProperty(null);
            refreshOffers();
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// PropertyCard — wide horizontal layout for the account area
// ---------------------------------------------------------------------------

interface PropertyCardProps {
  property: WishlistItemDisplay;
  activeOffer: Offer | null;
  isRemoving: boolean;
  onRemove: () => void;
  onScheduleViewing: () => void;
  onMakeOffer: () => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  activeOffer,
  isRemoving,
  onRemove,
  onScheduleViewing,
  onMakeOffer,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [imgError, setImgError] = useState(false);
  const getHref = useLocalizedHref();

  const handleHeartClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAnimating(true);
    onRemove();
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const shareUrl = `${window.location.origin}${getHref(`/properties/${property.entityId}`)}`;
    const shareData = {
      title: property.title,
      text: `Check out this property: ${property.title} - ${property.location}`,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
      }
    } catch {
      // User cancelled or share failed
    }
  };

  const hasImage = property.image && !imgError;
  const hasSpecs = property.beds != null || property.baths != null || property.area != null;
  const isSold = property.status === 'sold';

  const statusConfig = {
    active: { label: 'For Sale', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    sold: { label: 'Sold', className: 'bg-stone-100 text-stone-500 border-stone-200' },
    'under-offer': { label: 'Under Offer', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  } as const;

  const { label: statusLabel, className: statusClassName } = statusConfig[property.status];

  return (
    <div className="group bg-white rounded-2xl border border-stone-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
      <div className="flex flex-col md:flex-row">
        {/* ---------------------------------------------------------------- */}
        {/* Image                                                            */}
        {/* ---------------------------------------------------------------- */}
        <Link
          href={getHref(`/properties/${property.entityId}`)}
          className="relative w-full md:w-72 lg:w-80 shrink-0 aspect-4/3 md:aspect-auto md:min-h-[260px] overflow-hidden"
        >
          {hasImage ? (
            <Image
              src={property.image}
              alt={property.title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-700"
              unoptimized
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full bg-stone-100 flex items-center justify-center">
              <MapPin size={40} className="text-stone-300" />
            </div>
          )}

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-linear-to-t from-black/40 via-transparent to-transparent md:bg-linear-to-r md:from-transparent md:via-transparent md:to-transparent" />

          {/* Heart button */}
          <button
            onClick={handleHeartClick}
            disabled={isRemoving}
            className={cn(
              'absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center transition-all duration-300 shadow-md bg-white/90 backdrop-blur-sm text-red-500 hover:bg-white hover:scale-110',
              isAnimating && 'scale-125 ring-4 ring-red-100',
              isRemoving && 'opacity-50'
            )}
          >
            {isRemoving ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Heart size={16} className="fill-current" strokeWidth={0} />
            )}
          </button>

          {/* Status badge (mobile only — on image) */}
          <div className="absolute bottom-3 left-3 md:hidden">
            <span className={cn('px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg border', statusClassName)}>
              {statusLabel}
            </span>
          </div>
        </Link>

        {/* ---------------------------------------------------------------- */}
        {/* Content                                                          */}
        {/* ---------------------------------------------------------------- */}
        <div className="flex-1 flex flex-col p-5 md:p-6 min-w-0">
          {/* Top row: status + type + share */}
          <div className="flex items-center gap-2 mb-3">
            <span className={cn('hidden md:inline-flex px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg border', statusClassName)}>
              {statusLabel}
            </span>
            {property.propertyTypeName && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg border border-stone-200 bg-stone-50 text-stone-600">
                <Tag size={10} />
                {property.propertyTypeName}
              </span>
            )}
            <button
              onClick={handleShare}
              className="ml-auto h-8 w-8 rounded-full flex items-center justify-center text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors"
            >
              <Share2 size={14} />
            </button>
          </div>

          {/* Title & location */}
          <Link href={getHref(`/properties/${property.entityId}`)} className="group/title">
            <h3 className="text-lg font-semibold text-stone-900 leading-tight truncate group-hover/title:text-stone-700 transition-colors">
              {property.title}
            </h3>
          </Link>
          <p className="text-sm text-stone-500 flex items-center gap-1 mt-1 truncate">
            <MapPin size={13} className="shrink-0 text-stone-400" />
            {property.location}
          </p>

          {/* Specs row */}
          {hasSpecs && (
            <div className="flex items-center gap-4 mt-4 text-sm text-stone-600">
              {property.beds != null && (
                <div className="flex items-center gap-1.5">
                  <Bed size={15} className="text-stone-400" />
                  <span className="font-medium">{property.beds}</span>
                  <span className="text-stone-400 text-xs">Beds</span>
                </div>
              )}
              {property.baths != null && (
                <div className="flex items-center gap-1.5">
                  <Bath size={15} className="text-stone-400" />
                  <span className="font-medium">{property.baths}</span>
                  <span className="text-stone-400 text-xs">Baths</span>
                </div>
              )}
              {property.area != null && (
                <div className="flex items-center gap-1.5">
                  <Maximize size={15} className="text-stone-400" />
                  <span className="font-medium">{formatArea(property.area, property.areaUnit)}</span>
                </div>
              )}
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Active Offer Banner */}
          {activeOffer && (
            <OfferStatusBanner offer={activeOffer} />
          )}

          {/* Price + Actions */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mt-5 pt-4 border-t border-stone-100">
            {/* Price */}
            <div className="shrink-0">
              <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider block mb-0.5">
                Guide Price
              </span>
              <FormatPrice
                amount={property.price}
                currency={property.currency}
                size="lg"
                className="text-2xl font-light text-stone-900"
              />
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              {!isSold && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onScheduleViewing();
                    }}
                  >
                    <Calendar size={14} />
                    Schedule Viewing
                  </Button>
                  {activeOffer ? (
                    <Link href={getHref('/account/offers')}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 text-xs border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white"
                      >
                        <Gavel size={14} />
                        View Offer
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-xs"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onMakeOffer();
                      }}
                    >
                      <Send size={14} />
                      Make an Offer
                    </Button>
                  )}
                </>
              )}
              <Link href={getHref(`/properties/${property.entityId}`)}>
                <Button size="sm" className="gap-1.5 text-xs">
                  <ExternalLink size={14} />
                  View Details
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// OfferStatusBanner — shows active offer on a wishlist card
// ---------------------------------------------------------------------------

const offerStatusConfig: Record<string, { label: string; bg: string; text: string; border: string; icon: React.ElementType; pulse?: boolean }> = {
  COUNTERED: { label: 'Counter Received', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: AlertCircle, pulse: true },
  ACCEPTED: { label: 'Offer Accepted', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2 },
  ACCEPTED_CONDITIONAL: { label: 'Accepted (Conditional)', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2 },
  ACCEPTED_SUBJECT_TO_CONTRACT: { label: 'Accepted (STC)', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2 },
  REJECTED: { label: 'Offer Rejected', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', icon: XCircle },
  SUBMITTED: { label: 'Offer Submitted', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Send },
  RECEIVED: { label: 'Offer Received', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Send },
  UNDER_REVIEW: { label: 'Under Review', bg: 'bg-stone-50', text: 'text-stone-700', border: 'border-stone-200', icon: Gavel },
};

const OfferStatusBanner: React.FC<{ offer: Offer }> = ({ offer }) => {
  const getHref = useLocalizedHref();
  const config = offerStatusConfig[offer.status] ?? {
    label: offer.status.replace(/_/g, ' '),
    bg: 'bg-stone-50',
    text: 'text-stone-600',
    border: 'border-stone-200',
    icon: Gavel,
    pulse: false,
  };
  const Icon = config.icon;

  const formatAmount = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${currency} ${amount.toLocaleString()}`;
    }
  };

  return (
    <Link href={getHref('/account/offers')} className="block mt-4">
      <div className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 hover:shadow-md cursor-pointer group/offer',
        config.bg, config.border
      )}>
        <div className="relative">
          <Icon size={18} className={config.text} />
          {config.pulse && (
            <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn('text-xs font-bold uppercase tracking-wider', config.text)}>
              {config.label}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-sm font-semibold text-stone-900">
              {formatAmount(offer.price.amount, offer.price.currency)}
            </span>
            {offer.counterPrice && (
              <>
                <ArrowRight size={12} className="text-stone-400" />
                <span className="text-sm font-semibold text-amber-600">
                  {formatAmount(offer.counterPrice.amount, offer.counterPrice.currency)}
                </span>
              </>
            )}
          </div>
        </div>
        <ArrowRight size={16} className="text-stone-400 group-hover/offer:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
};

// ---------------------------------------------------------------------------
// OfferModal — wraps OfferForm in a modal overlay
// ---------------------------------------------------------------------------

const OfferModal: React.FC<{
  property: WishlistItemDisplay;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ property, onClose, onSuccess }) => {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!property.listingId) {
    return (
      <>
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-[fadeIn_0.2s_ease-out]"
          onClick={onClose}
        />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full pointer-events-auto animate-[fadeIn_0.2s_ease-out,slideUp_0.3s_ease-out]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 text-center">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-stone-400 hover:text-stone-900 hover:bg-stone-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <AlertCircle size={28} className="text-stone-500" />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">Unable to Make Offer</h3>
              <p className="text-stone-500 text-sm">
                This property does not have an active listing. Please contact the agent directly for more information.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-[fadeIn_0.2s_ease-out]"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-3xl shadow-2xl w-full max-w-lg pointer-events-auto animate-[fadeIn_0.2s_ease-out,slideUp_0.3s_ease-out] max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-stone-100 shrink-0">
            <div>
              <h2 className="text-xl font-bold text-stone-900">Make an Offer</h2>
              {property.title && (
                <p className="text-sm text-stone-500 mt-0.5">{property.title}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-stone-400 hover:text-stone-900 hover:bg-stone-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1 p-6">
            <OfferForm
              listingId={property.listingId}
              listingTitle={property.title}
              {...(property.askingPrice != null ? { askingPrice: property.askingPrice } : {})}
              askingCurrency={property.askingCurrency ?? property.currency}
              onSuccess={() => onSuccess()}
            />
          </div>
        </div>
      </div>

      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>
  );
};
